// Configuration de l'URL de base de l'API
const getApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL
  if (envUrl) {
    return envUrl
  }
  // Fallback production sûr (évite localhost bloqué par CSP)
  return 'https://api.agri-wise.com/api'
}

const API_BASE_URL = getApiBaseUrl()

class ApiService {
  private token: string | null = null

  constructor() {
    // Essayer localStorage d'abord, puis sessionStorage comme fallback
    this.token = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (this.token && !localStorage.getItem('token')) {
      // Si le token est dans sessionStorage mais pas dans localStorage, le copier
      localStorage.setItem('token', this.token)
    }
  }

  // Méthode pour forcer la mise à jour du token depuis localStorage et sessionStorage
  refreshToken() {
    this.token = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (this.token && !localStorage.getItem('token')) {
      // Si le token est dans sessionStorage mais pas dans localStorage, le copier
      localStorage.setItem('token', this.token)
    }
  }

  setToken(token: string) {
    this.token = token
    // Stocker dans localStorage ET sessionStorage pour plus de résilience
    localStorage.setItem('token', token)
    sessionStorage.setItem('token', token)
  }

  clearToken() {
    this.token = null
    localStorage.removeItem('token')
    sessionStorage.removeItem('token')
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
      ...options.headers,
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      })

      const data = await response.json()

      if (!response.ok) {
        // Si le token est invalide ou l'utilisateur n'existe plus, on déconnecte
        if (response.status === 401 &&
          endpoint !== '/auth/login' &&
          endpoint !== '/auth/register' &&
          (data.message?.includes("n'existe plus") ||
            data.error?.includes("n'existe plus") ||
            data.message?.includes('Veuillez fournir un token') ||
            data.error?.includes('Veuillez fournir un token'))
        ) {
          this.clearToken()
          // Éviter la boucle de redirection
          if (!window.location.pathname.includes('/login')) {
            setTimeout(() => {
              window.location.href = '/login'
            }, 100)
          }
        }
        const apiError = new Error(data.message || data.error || 'Une erreur est survenue') as Error & { status?: number }
        apiError.status = response.status
        throw apiError
      }

      return data.data || data
    } catch (error) {
      console.error('API Error:', error)
      throw error
    }
  }

  // Auth
  async login(identifier: string, password: string) {
    const response = await this.request<any>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password }),
    })
    if (response.token) {
      this.setToken(response.token)
    }
    return response
  }

  async register(data: { nom: string; prenom: string; email: string; telephone: string; password: string }) {
    const response = await this.request<any>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    if (response.token) {
      this.setToken(response.token)
    }
    return response
  }

  async getProfile() {
    const response = await this.request<any>('/auth/profile')
    return response.user || response
  }

  // Client Password Setup
  async verifyPhoneForPasswordSetup(telephone: string) {
    return this.request<any>('/client-password-setup/verify-phone', {
      method: 'POST',
      body: JSON.stringify({ telephone }),
    })
  }

  async createPasswordForExistingClient(telephone: string, password: string, confirmPassword: string) {
    const response = await this.request<any>('/client-password-setup/create-password', {
      method: 'POST',
      body: JSON.stringify({ telephone, password, confirmPassword }),
    })
    if (response.token) {
      this.setToken(response.token)
    }
    return response
  }

  // Forgot Password
  async verifyClientForPasswordReset(telephone: string, nomComplet: string) {
    return this.request<any>('/forgot-password/verify-client', {
      method: 'POST',
      body: JSON.stringify({ telephone, nom_complet: nomComplet }),
    })
  }

  async resetPassword(telephone: string, nomComplet: string, newPassword: string, confirmPassword: string) {
    const response = await this.request<any>('/forgot-password/reset-password', {
      method: 'POST',
      body: JSON.stringify({
        telephone,
        nom_complet: nomComplet,
        new_password: newPassword,
        confirm_password: confirmPassword
      }),
    })
    if (response.token) {
      this.setToken(response.token)
    }
    return response
  }

  async sendEmailOTP(email: string) {
    return this.request<any>('/forgot-password/send-email-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  async verifyEmailOTP(email: string, otp: string) {
    return this.request<any>('/forgot-password/verify-email-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    })
  }

  async resetPasswordByToken(resetToken: string, newPassword: string, confirmPassword: string) {
    const response = await this.request<any>('/forgot-password/reset-password-by-token', {
      method: 'POST',
      body: JSON.stringify({
        resetToken,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),
    })
    if (response.token) {
      this.setToken(response.token)
    }
    return response
  }

  // Trajets
  async getTrajets(params?: { ville_depart?: string; ville_arrivee?: string; date?: string }) {
    const query = params ? '?' + new URLSearchParams(params as any).toString() : ''
    return this.request(`/trajets${query}`)
  }

  async getTrajetById(id: string) {
    return this.request(`/trajets/${id}`)
  }

  // Trajets avec horaires disponibles (pour la réservation)
  async getTrajetsWithSchedules(date: string) {
    return this.request(`/public/trajets-with-schedules?date=${date}&mobile=true`)
  }

  // Horaires disponibles pour une date
  async getAvailableSchedules(date: string) {
    return this.request(`/horaires-base/available/${date}`)
  }

  // Réservations
  async createReservation(data: any) {
    return this.request('/reservations/smart', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getMyReservations() {
    return this.request('/reservations/my')
  }

  async getReservationById(id: string) {
    return this.request(`/reservations/${id}`)
  }

  async cancelReservation(id: string) {
    return this.request(`/reservations/${id}/cancel`, {
      method: 'PUT',
    })
  }

  // Colis
  async trackPackage(trackingNumber: string) {
    return this.request(`/colis/track/${trackingNumber}`)
  }

  async getMyPackages() {
    return this.request('/baggage/my')
  }

  // Paiements
  async initializePayment(data: { reservationId: string; amount: number; method: string }) {
    return this.request('/payments/initialize', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async verifyPayment(transactionId: string) {
    return this.request(`/payments/verify/${transactionId}`)
  }

  // Notifications
  async getNotifications() {
    return this.request('/notifications')
  }

  async getMyNotifications() {
    return this.request('/notifications/my')
  }

  async getUnreadNotificationCount(): Promise<number> {
    try {
      // request() retourne déjà data.data || data → on reçoit { unreadCount: number }
      const response = await this.request('/notifications/unread-count') as { unreadCount?: number }
      const count = response?.unreadCount
      return typeof count === 'number' ? count : Number(count) || 0
    } catch {
      return 0
    }
  }

  // Galerie de photos (route publique)
  async getGalleryPhotos() {
    try {
      const response = await fetch(`${API_BASE_URL}/gallery`)
      const data = await response.json()
      const photos = data?.data || []

      // Construire les URLs complètes pour les images de manière robuste
      const apiBase = import.meta.env.VITE_API_URL || 'https://api.agri-wise.com/api'
      const originUrl = new URL(apiBase, window.location.origin)
      originUrl.pathname = originUrl.pathname.replace(/\/?api\/?$/, '/')

      return photos.map((photo: any) => {
        let url = photo.image_url
        try {
          const absolute = new URL(url, originUrl.toString())
          url = absolute.href
        } catch { }
        return { ...photo, url }
      })
    } catch (error) {
      console.error('Erreur lors du chargement des photos:', error)
      return []
    }
  }

  // Récupérer les trajets groupés par ville de départ (PUBLIC)
  async getTrajetsGroupes(date?: string) {
    const params = new URLSearchParams()
    if (date) {
      params.append('date', date)
    }
    params.append('actif', 'true')
    params.append('mobile', 'true')

    const queryString = params.toString()
    return this.request(`/public/trajets/grouped${queryString ? `?${queryString}` : ''}`)
  }

  async updateClientProfile(data: { nom?: string; prenom?: string; telephone?: string; email?: string; sexe?: string }) {
    return this.request<any>('/auth/client-profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async changeClientPassword(currentPassword: string, newPassword: string) {
    return this.request<any>('/auth/client-change-password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    })
  }

  async markNotificationAsRead(id: string) {
    return this.request(`/notifications/${id}/read`, {
      method: 'PUT',
    })
  }

  // Company
  async getCompanyInfo() {
    return this.request('/company')
  }

  // Clients (PUBLIC - pour les clients)
  async getClientByPhone(telephone: string) {
    return this.request(`/public/clients/phone/${telephone}`)
  }

  async createClient(data: {
    nom: string
    prenom: string
    telephone: string
    email?: string
  }) {
    return this.request('/public/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Méthodes de paiement (PUBLIC - pour les clients)
  async getPaymentMethods() {
    return this.request('/public/payment-methods')
  }

  // Sièges disponibles (PUBLIC - pour les clients)
  async getSiegesDisponibles(date_voyage: string, heure_depart: string, trajet_id: string) {
    return this.request(`/public/sieges-disponibles?date_voyage=${date_voyage}&heure_depart=${heure_depart}&trajet_id=${trajet_id}&mobile=true`)
  }

  // Récupérer uniquement les horaires d'un trajet spécifique (OPTIMISÉ)
  async getTrajetHoraires(trajetId: string, date: string) {
    return this.request(`/public/trajet/${trajetId}/horaires?date=${date}&mobile=true`)
  }

  // Endpoint optimisé pour le Dashboard avec paramètre ville optionnel
  async getDashboardTrajets(date: string, villeDepart?: string): Promise<{
    success: boolean;
    date: string;
    isPastDate: boolean;
    trajets: Array<{
      id: string;
      nom: string;
      ville_depart: string;
      ville_arrivee: string;
      prix_aller: number;
      prix_aller_retour: number;
      horaires: Array<{
        heure_depart: string;
        total_sieges: number;
        sieges_occupes: number;
        sieges_libres: number;
        nombre_bus: number;
        buses: Array<{
          id: string;
          nom: string;
          total_sieges: number;
          sieges_occupes: number;
          sieges_libres: number;
        }>;
      }>;
    }>;
    total: number;
  }> {
    const params = new URLSearchParams({ date })
    if (villeDepart) {
      params.append('ville_depart', villeDepart)
    }
    params.append('mobile', 'true')
    return this.request(`/public/dashboard/trajets?${params.toString()}`)
  }

  // Récupérer toutes les villes de départ disponibles (endpoint rapide)
  async getVillesDepart(): Promise<string[]> {
    try {
      // Utiliser un endpoint rapide qui retourne uniquement les villes distinctes
      const data = await this.request<{ success: boolean; villes: string[] }>('/public/villes-depart')
      if (data.success && Array.isArray(data.villes)) {
        return data.villes
      }
      return []
    } catch (error) {
      console.error('Erreur lors de la récupération des villes:', error)
      return []
    }
  }

  // Réservation publique (pour les clients)
  async createPublicReservation(data: {
    client_nom: string
    client_prenom: string
    client_telephone: string
    client_email?: string
    trajet_id: string
    date_voyage: string
    heure_depart: string
    numero_siege: number
    type_ticket_id: number
    payment_method_id: number
    notes?: string
  }) {
    return this.request('/public/reservations?mobile=true', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Réservation intelligente (pour les employés)
  async createSmartReservation(data: {
    client_id: string
    trajet_id: string
    date_voyage: string
    heure_depart: string
    numero_siege: number
    lieu_embarquement_id?: string | null
    type_ticket_id: number
    payment_method_id: number
    voyage_retour_id?: string | null
    notes?: string
  }) {
    return this.request('/reservations/smart', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }
}

export const apiService = new ApiService()
export default apiService

